import { useSnackbar } from 'contexts/snackbar';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const GenerateAssignmentId = memo(({ generatedId, isGenerating = false, label, showGenerate = false, onGenerate }) => {
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const normalizedId = typeof generatedId === 'string' && generatedId.trim() ? generatedId.trim() : null;

    const handleCopy = async (event) => {
        event.stopPropagation();
        if (!normalizedId) return;

        try {
            await navigator.clipboard.writeText(normalizedId);
            message.success(t('message.assignment-id-copied-success', 'Assignment ID copied to clipboard.'));
        } catch (error) {
            message.error(t('message.assignment-id-copy-error', 'Unable to copy the assignment ID.'));
        }
    };

    const handleGenerate = (event) => {
        event.stopPropagation();
        return onGenerate?.();
    };

    return (
        <Stack alignItems={'center'} direction={'row'} sx={{ minWidth: 0 }} spacing={2}>
            <Text component={'span'}>{label ?? t('general-section.assignment-id-label', 'Assignment ID')}</Text>
            {showGenerate && !normalizedId ? (
                <Button
                    loading={isGenerating}
                    size={'small'}
                    startIcon={<Icon kind={'refresh'} />}
                    variant={'secondary'}
                    onClick={handleGenerate}
                >
                    {t('common:controls.generate', 'Generate')}
                </Button>
            ) : (
                <Stack alignItems={'center'} direction={'row'} sx={{ minWidth: 0 }} spacing={1}>
                    <Text
                        color={normalizedId ? 'primary' : 'light'}
                        component={'span'}
                        sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
                    >
                        {normalizedId || '--'}
                    </Text>
                    <IconButton
                        aria-label={t('common:controls.copy-assignment-id', 'Copy assignment ID')}
                        disabled={!normalizedId}
                        kind={'copy'}
                        size={'small'}
                        variant={'square'}
                        onClick={handleCopy}
                    />
                </Stack>
            )}
        </Stack>
    );
});

export default GenerateAssignmentId;
